/**
 * LogoSplash — animierter Startbildschirm mit Zeicheneffekt.
 *
 * Die Otter-Kontur zeichnet sich Strich fuer Strich selbst, Augen und Nase
 * poppen hinein, danach ein kurzes Zwinkern. Hintergrund: plain weiss,
 * im Dunkelmodus invertiert (schwarzer Grund, helle Linien).
 *
 * Herkunft: Der Entwurf stammt aus "Logoanimation mit Zeicheneffekt"
 * (Claude-Design-Canvas). Dort haengt die Animation an einem 69-KB-Runtime
 * (support.js, CompositionStage/useComposition). Hier sind nur die SVG-Pfade
 * und das Timing uebernommen — gerechnet wird per CSS-Keyframes, damit die
 * App weder ein Extra-Paket noch einen Dauer-Renderloop mitschleppt.
 *
 * Tempo aendern: nur SPEED anfassen. 1 = Originaltempo aus dem Entwurf
 * (~3,2 s reines Zeichnen), 0.55 = das aktuelle, app-taugliche Tempo.
 */
import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

// --- Tempo -----------------------------------------------------------------
const SPEED = 0.55;
const sec = (t) => `${+(t * SPEED).toFixed(3)}s`;

// --- Zeitachse (in Original-Einheiten des Entwurfs) ------------------------
const EYE_AT = 2.12;
const EYE_DUR = 0.32;
const NOSE_AT = 2.28;
const NOSE_DUR = 0.34;
const ZOOM_END = 2.0;
const BLINK_AT = 3.45;
const BLINK_DUR = 0.56;
const TILT_DUR = 0.8;
const BREATHE_AT = 3.6;

/** Wann die Animation inhaltlich fertig ist (nach dem Zwinkern), in ms. */
const ANIM_MS = Math.round((BLINK_AT + BLINK_DUR) * SPEED * 1000) + 120;
/** Ausblenden. */
const FADE_MS = 420;
/** Notbremse: Falls der Auth-Start haengt, verschwindet der Splash trotzdem. */
const MAX_WAIT_MS = 8000;

// --- Geometrie -------------------------------------------------------------
const P = {
  head: 'M 638 156 C 552 158 476 196 440 252 C 404 308 356 384 350 468 C 348 546 388 606 418 624 C 430 646 462 660 505 658 C 548 656 578 648 594 632 C 608 616 616 598 625 578 C 634 598 642 616 656 632 C 672 648 704 662 748 660 C 790 658 818 650 835 632 C 864 600 898 558 896 500 C 894 430 880 344 846 288 C 822 224 740 154 638 156',
  earL: 'M 396 292 C 340 284 300 316 304 356 C 308 398 340 418 374 414',
  earR: 'M 893 334 C 940 326 962 366 957 400 C 952 438 926 470 894 476',
  body: 'M 400 634 C 396 720 390 830 390 900 C 390 1000 470 1100 628 1100 C 786 1100 848 1000 848 900 C 848 830 848 720 848 634',
  wL1: 'M 402 497 L 470 462',
  wL2: 'M 435 564 L 502 523',
  wR1: 'M 843 493 L 780 461',
  wR2: 'M 816 548 L 742 512',
  wave1: 'M 484 872 C 502 898 520 912 543 911 C 572 909 596 866 618 853 C 640 862 660 902 685 911 C 712 918 762 900 789 878',
  wave2: 'M 478 976 C 496 1004 518 1014 542 1013 C 570 1011 590 970 610 962 C 632 968 648 1006 669 1013 C 694 1021 730 998 752 968',
  nose: 'M 554 450 C 552 388 695 388 693 450 C 692 492 656 518 623 518 C 590 518 555 492 554 450',
  lid: 'M 456 378 C 472 401 502 401 516 375',
};

/** [Pfad, Start, Ende, Strichstaerke] — Reihenfolge = Zeichenreihenfolge. */
const STROKES = [
  [P.head, 0.10, 1.62, 31],
  [P.earL, 1.25, 1.64, 31],
  [P.earR, 1.36, 1.75, 31],
  [P.body, 1.60, 2.62, 31],
  [P.wL1, 2.50, 2.70, 24],
  [P.wL2, 2.58, 2.78, 24],
  [P.wR1, 2.54, 2.74, 24],
  [P.wR2, 2.62, 2.82, 24],
  [P.wave1, 2.72, 3.08, 26],
  [P.wave2, 2.88, 3.24, 26],
];

const EYES = [
  { cx: 486, cy: 385, blinks: true },
  { cx: 766, cy: 385, blinks: false },
];

/**
 * Die Otter-Geometrie fuer andere Zeichner: `focus/PulsatingAura.js` malt den
 * Otter damit als weisse Linie in einen Canvas (Path2D), ohne PNG und ohne
 * CSS-Filter. Aendert sich das Logo, aendert es sich an genau einer Stelle.
 */
export const OTTER_GEOMETRY = { paths: P, strokes: STROKES, eyes: EYES, viewBox: 1254 };

// --- Styles ----------------------------------------------------------------
// Bewusst als <style>-Tag in der Komponente: Der Splash ist die allererste
// Sache, die gemalt wird — ein separates CSS-File waere ein zusaetzlicher
// Ladeschritt genau an der Stelle, an der es auf Millisekunden ankommt.
const CSS = `
.eo-splash {
  position: fixed; inset: 0; z-index: 99999;
  display: flex; align-items: center; justify-content: center;
  background: #ffffff; --eo-ink: #111827;
  transition: opacity ${FADE_MS}ms ease-out, transform ${FADE_MS}ms ease-out;
}
.dark .eo-splash { background: #000000; --eo-ink: #f9fafb; }
/* pointer-events aus, sobald das Ausblenden laeuft: Waehrend der Blende ist
   der Splash schon durchsichtig, wuerde aber weiter jeden Fingertipp
   schlucken — und faengt er sich je aus dem DOM zu gehen, waere die App
   komplett unbedienbar. */
.eo-splash.eo-out { opacity: 0; transform: scale(1.06); pointer-events: none; }

.eo-svg { width: min(29vw, 22vh, 160px); height: auto; overflow: visible; }

/* Verschachtelte Wrapper, weil jede Ebene eine eigene transform-Animation
   faehrt (Zoom / Neigung beim Zwinkern / Atmen) und CSS pro Element nur
   eine transform-Animation gleichzeitig kennt. */
.eo-zoom    { animation: eo-zoom ${sec(ZOOM_END)} cubic-bezier(.25,1,.5,1) both; }
.eo-tilt    { animation: eo-tilt ${sec(TILT_DUR)} ease-in-out ${sec(BLINK_AT)} both; }
.eo-breathe { animation: eo-breathe 3.3s ease-in-out ${sec(BREATHE_AT)} infinite; }

.eo-line {
  fill: none; stroke: var(--eo-ink);
  stroke-linecap: round; stroke-linejoin: round;
  /* Luecke bewusst 2 statt 1 (pathLength ist 1): Bei "1" faellt der Anfang des
     naechsten Strichs exakt auf das Pfadende, und stroke-linecap:round malt dort
     seine runde Kappe - auf dem iPhone standen dadurch von Anfang an dicke Punkte
     an den Enden der Schnurrhaare. Mit Luecke 2 liegt der naechste Strich
     ausserhalb des Pfades. (Chrome zeichnet die Kappe nicht, WebKit schon.) */
  stroke-dasharray: 1 2; stroke-dashoffset: 1;
  animation: eo-draw var(--eo-dur) cubic-bezier(.37,0,.63,1) var(--eo-delay) forwards;
}
.eo-pop {
  transform: scale(0); transform-box: view-box;
  transform-origin: var(--eo-ox) var(--eo-oy);
  animation: eo-pop var(--eo-dur) cubic-bezier(.34,1.56,.64,1) var(--eo-delay) forwards;
}
.eo-blink {
  transform-box: view-box; transform-origin: var(--eo-ox) var(--eo-oy);
  animation: eo-blink ${sec(BLINK_DUR)} cubic-bezier(.33,1,.68,1) ${sec(BLINK_AT)} both;
}
.eo-lid {
  fill: none; stroke: var(--eo-ink); stroke-linecap: round; stroke-linejoin: round;
  opacity: 0;
  animation: eo-lid ${sec(BLINK_DUR)} linear ${sec(BLINK_AT)} both;
}

@keyframes eo-draw    { to { stroke-dashoffset: 0; } }
@keyframes eo-pop     { from { transform: scale(0); } to { transform: scale(1); } }
@keyframes eo-zoom    { from { transform: scale(1.09); } to { transform: scale(1); } }
@keyframes eo-tilt    { 0%, 100% { transform: rotate(0); } 50% { transform: rotate(-2.6deg); } }
@keyframes eo-breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.014); } }
@keyframes eo-blink   { 0% { transform: scaleY(1); } 27% { transform: scaleY(.04); } 50% { transform: scaleY(.04); } 100% { transform: scaleY(1); } }
@keyframes eo-lid     { 0% { opacity: 0; } 27% { opacity: 1; } 50% { opacity: 1; } 78% { opacity: 0; } 100% { opacity: 0; } }

/* Wer Bewegung reduziert haben will, bekommt das fertige Logo ohne Zeichnen. */
@media (prefers-reduced-motion: reduce) {
  .eo-zoom, .eo-tilt, .eo-breathe, .eo-line, .eo-pop, .eo-blink, .eo-lid { animation: none !important; }
  .eo-line { stroke-dashoffset: 0; }
  .eo-pop  { transform: scale(1); }
  .eo-lid  { opacity: 0; }
}
`;

/**
 * Nur das gezeichnete Logo — ohne Vollbild-Overlay.
 * Auch einzeln verwendbar (z. B. auf der Landingpage).
 */
export const OtterLogoDraw = ({ className = '' }) => (
  <div className={`eo-zoom ${className}`}>
    <style>{CSS}</style>
    <div className="eo-tilt">
      <div className="eo-breathe">
        <svg className="eo-svg" viewBox="0 0 1254 1254" role="img" aria-label="EachOtter">
          {STROKES.map(([d, from, to, width], i) => (
            <path
              key={i}
              className="eo-line"
              d={d}
              pathLength="1"
              strokeWidth={width}
              style={{ '--eo-delay': sec(from), '--eo-dur': sec(to - from) }}
            />
          ))}

          <g
            className="eo-pop"
            style={{ '--eo-ox': '623px', '--eo-oy': '480px', '--eo-delay': sec(NOSE_AT), '--eo-dur': sec(NOSE_DUR) }}
          >
            <path d={P.nose} fill="var(--eo-ink)" />
          </g>

          {EYES.map(({ cx, cy, blinks }) => {
            const origin = { '--eo-ox': `${cx}px`, '--eo-oy': `${cy}px` };
            return (
              <g
                key={cx}
                className="eo-pop"
                style={{ ...origin, '--eo-delay': sec(EYE_AT), '--eo-dur': sec(EYE_DUR) }}
              >
                <ellipse
                  className={blinks ? 'eo-blink' : undefined}
                  cx={cx}
                  cy={cy}
                  rx={27.5}
                  ry={30}
                  fill="var(--eo-ink)"
                  style={blinks ? origin : undefined}
                />
              </g>
            );
          })}

          {/* Geschlossenes Lid — nur waehrend des Zwinkerns sichtbar. */}
          <path className="eo-lid" d={P.lid} strokeWidth={24} />
        </svg>
      </div>
    </div>
  </div>
);

// --- Anzeige-Entscheidung --------------------------------------------------
// Modulweit gemerkt, damit weder React StrictMode (doppelte Initializer) noch
// ein Re-Mount die Entscheidung ein zweites Mal trifft.
let decision = null;

const shouldShowSplash = () => {
  if (decision !== null) return decision;
  if (Capacitor.isNativePlatform()) {
    // Native App: jeder Kaltstart bekommt die Animation.
    decision = true;
    return decision;
  }
  try {
    // Web: einmal pro Browser-Sitzung. Sonst kostet jeder Reload knapp 3 s.
    decision = !sessionStorage.getItem('eo-splash-seen');
    sessionStorage.setItem('eo-splash-seen', '1');
  } catch (e) {
    // Kein sessionStorage (Privatmodus, gesperrte Cookies) — dann eben zeigen.
    decision = true;
  }
  return decision;
};

/**
 * Vollbild-Startbildschirm. Bleibt stehen, bis BEIDES stimmt:
 * die Animation ist durch UND die App meldet sich per 'eachotter:app-ready'.
 * So sieht niemand eine halbe Animation und niemand eine leere Seite.
 */
const LogoSplash = () => {
  const [visible] = useState(shouldShowSplash);
  const [phase, setPhase] = useState('in');
  const [animDone, setAnimDone] = useState(false);
  const [appReady, setAppReady] = useState(false);

  // Nativen Capacitor-Splash wegnehmen, sobald unser eigener gemalt ist.
  // Zwei rAF, damit der erste Frame mit der Animation wirklich auf dem Schirm
  // ist — sonst blitzt zwischen beiden Splashes der leere WebView durch.
  useEffect(() => {
    if (!visible || !Capacitor.isNativePlatform()) return undefined;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => {});
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const t = setTimeout(() => setAnimDone(true), ANIM_MS);
    return () => clearTimeout(t);
  }, [visible]);

  // Solange der Splash steht, haelt die Seite ihre Einblend-Animationen an
  // (index.css: .eo-splash-active) — sonst liefe z. B. der Hero der
  // Landingpage verdeckt ab. Freigabe, sobald der Splash ausblendet.
  useEffect(() => {
    if (!visible || phase !== 'in') return undefined;
    const html = document.documentElement;
    html.classList.add('eo-splash-active');
    return () => html.classList.remove('eo-splash-active');
  }, [visible, phase]);

  useEffect(() => {
    if (!visible) return undefined;
    if (window.__eachotterAppReady) {
      setAppReady(true);
      return undefined;
    }
    const onReady = () => setAppReady(true);
    window.addEventListener('eachotter:app-ready', onReady);
    const bail = setTimeout(() => setAppReady(true), MAX_WAIT_MS);
    return () => {
      window.removeEventListener('eachotter:app-ready', onReady);
      clearTimeout(bail);
    };
  }, [visible]);

  // ACHTUNG: `phase` darf hier NICHT in die Abhaengigkeiten. Der Effekt setzt
  // `phase` selbst — stuende es in der Liste, raeumte React den gerade
  // gestellten Timer beim naechsten Rendern sofort wieder ab. Der Splash kaeme
  // dann nie auf 'gone', bliebe unsichtbar (opacity 0) ueber der App liegen und
  // wuerde jeden Fingertipp abfangen.
  useEffect(() => {
    if (!visible || !animDone || !appReady) return undefined;
    setPhase('out');
    const t = setTimeout(() => setPhase('gone'), FADE_MS);
    return () => clearTimeout(t);
  }, [visible, animDone, appReady]);

  if (!visible || phase === 'gone') return null;

  return (
    <div className={`eo-splash${phase === 'out' ? ' eo-out' : ''}`} aria-hidden="true">
      <OtterLogoDraw />
    </div>
  );
};

export default LogoSplash;
